// Canonical ActionConfig factories — keep the action ladder DRY across
// every entity view. The shape (id, icon, danger, confirm key set) is
// fixed; consumers pass only the per-entity localised strings + handler.
//
// Used by `view.quickActions` / `view.primaryActions` / `view.moreActions`
// builders inside the per-entity view-config hooks.

import React from "react";
import { CopyOutlined, DeleteOutlined, LinkOutlined } from "@ant-design/icons";
import type { ActionConfig } from "@ds/config/entityConfig";

/**
 * Standard "Kopiuj link" quick action. Renders `<LinkOutlined />` with id "copy-link".
 */
export function copyLinkAction(opts: {
  label: string;
  onClick: () => void;
}): ActionConfig {
  return {
    id: "copy-link",
    label: opts.label,
    icon: <LinkOutlined />,
    onClick: opts.onClick,
  };
}

/**
 * Standard "Duplikuj" action. Used as a primary or more action depending on
 * the entity. Caller controls loading/disabled state for the duplicate-in-flight
 * indicator.
 */
export function duplicateAction(opts: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
}): ActionConfig {
  return {
    id: "duplicate",
    label: opts.label,
    icon: <CopyOutlined />,
    disabled: opts.disabled,
    loading: opts.loading,
    onClick: opts.onClick,
  };
}

/**
 * Standard destructive "Usuń" more-action with confirm modal. `okText`
 * defaults to the action label (operator confirms with the same word that
 * triggered the modal).
 */
export function deleteAction(opts: {
  label: string;
  confirmTitle: string;
  confirmDescription?: string;
  onClick: () => void;
}): ActionConfig {
  return {
    id: "delete",
    label: opts.label,
    icon: <DeleteOutlined />,
    danger: true,
    confirm: {
      title: opts.confirmTitle,
      description: opts.confirmDescription,
      okText: opts.label,
    },
    onClick: opts.onClick,
  };
}
